// components/Dashboard/tabs/platforms/TiktokChannels.js
import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import './TiktokChannels.css';

const TiktokChannels = forwardRef(({ 
  showMessage, 
  API_URL,
  onNavigateToLibrary,
  onNavigateToAutomation
}, ref) => {
  const [tiktokAccounts, setTiktokAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [automationRules, setAutomationRules] = useState([]);
  const [tags, setTags] = useState([]);

  useImperativeHandle(ref, () => ({
    handleRefresh: refreshAccounts,
    handleAddChannel: handleConnectTikTok
  }));

  const fetchAutomationRules = async () => {
    try {
      const response = await fetch(`${API_URL}/automation`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      if (data.success) setAutomationRules(data.rules || []);
    } catch (error) {
      console.error('Otomasyon kuralları yüklenemedi:', error);
    }
  };

  const fetchTags = async () => {
    try {
      const response = await fetch(`${API_URL}/tags`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      if (data.success) setTags(data.tags || []);
    } catch (error) {
      console.error('Etiketler yüklenemedi:', error);
    }
  };

  const findMatchingAutomationRules = (accountId) => {
    return automationRules.filter(rule => {
      if (!rule.enable_tiktok_upload || rule.is_active !== 1) return false;
      if (!rule.tiktok_account_ids) return false;
      try {
        const ids = typeof rule.tiktok_account_ids === 'string'
          ? JSON.parse(rule.tiktok_account_ids) : rule.tiktok_account_ids;
        if (Array.isArray(ids)) return ids.includes(accountId);
      } catch (e) { }
      return false;
    });
  };

  const getAccountAutomationSchedule = (accountId) => {
    const accountRules = findMatchingAutomationRules(accountId);
    if (accountRules.length === 0) return null;
    let allTimes = [];
    accountRules.forEach(rule => {
      if (rule.daily_times) {
        try {
          const times = JSON.parse(rule.daily_times);
          if (Array.isArray(times) && times.length > 0) allTimes = [...allTimes, ...times];
        } catch (e) { }
      }
    });
    allTimes.sort((a, b) => a.hour !== b.hour ? a.hour - b.hour : a.minute - b.minute);
    const uniqueTimes = allTimes.filter((time, index, self) =>
      index === self.findIndex(t => t.hour === time.hour && t.minute === time.minute)
    );
    const now = new Date();
    let nextTimeIndex = uniqueTimes.findIndex(t =>
      t.hour > now.getHours() || (t.hour === now.getHours() && t.minute > now.getMinutes())
    );
    if (nextTimeIndex === -1 && uniqueTimes.length > 0) nextTimeIndex = 0;
    return { allTimes: uniqueTimes, nextTimeIndex, rulesCount: accountRules.length };
  };

  const getAccountActiveAutomationTags = (accountId) => {
    const accountRules = findMatchingAutomationRules(accountId);
    let allTagNames = [];
    accountRules.forEach(rule => {
      if (rule.tags) allTagNames = [...allTagNames, ...rule.tags.split(',').map(t => t.trim())];
    });
    return [...new Set(allTagNames)]
      .map(name => tags.find(tag => tag.name.toLowerCase() === name.toLowerCase()))
      .filter(Boolean);
  };

  const handleAutomationClick = (accountId, e) => {
    e.stopPropagation();
    const accountRules = findMatchingAutomationRules(accountId);
    if (accountRules.length > 0 && onNavigateToAutomation) onNavigateToAutomation(accountRules[0].id);
  };

  const handleTagClick = (tagId, e) => {
    e.stopPropagation();
    if (onNavigateToLibrary) onNavigateToLibrary(tagId);
  };

  const fetchTiktokAccounts = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/platforms/tiktok/accounts`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (!response.ok) { setTiktokAccounts([]); return; }
      const data = await response.json();
      setTiktokAccounts(data.accounts || []);
    } catch (error) {
      console.error('TikTok hesapları yüklenirken hata:', error);
      showMessage('TikTok hesapları yüklenemedi', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleConnectTikTok = async () => {
    try {
      setConnecting(true);
      const response = await fetch(`${API_URL}/platforms/tiktok/connect`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({})
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Bağlantı başlatılamadı');
      if (data.authUrl) {
        showMessage('TikTok\'a yönlendiriliyorsunuz...', 'info');
        window.location.href = data.authUrl;
      }
    } catch (error) {
      if (error.message.includes('Profil limiti')) {
        showMessage('❌ Profil limiti dolmuş. Planınızı yükseltin.', 'error');
      } else {
        showMessage(error.message || 'Bağlantı başlatılamadı', 'error');
      }
    } finally {
      setConnecting(false);
    }
  };

  const disconnectAccount = async (accountId, username) => {
    if (!window.confirm(`@${username} hesabının bağlantısını kaldırmak istediğinizden emin misiniz?`)) return;
    try {
      const response = await fetch(`${API_URL}/platforms/accounts/${accountId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (!response.ok) throw new Error('Bağlantı kaldırılamadı');
      const data = await response.json();
      showMessage(data.message || `@${username} hesap bağlantısı kaldırıldı`, 'success');
      fetchTiktokAccounts();
    } catch (error) {
      showMessage('Bağlantı kaldırılamadı', 'error');
    }
  };

  const refreshAccounts = async () => {
    await fetchTiktokAccounts();
    await fetchAutomationRules();
    await fetchTags();
    showMessage('Hesaplar yenilendi', 'success');
  };

  const formatCount = (count) => {
    if (!count) return '0';
    if (count >= 1000000) return (count / 1000000).toFixed(1) + 'M';
    if (count >= 1000) return (count / 1000).toFixed(1) + 'K';
    return count.toString();
  };

  const formatLastUpdate = (dateString) => {
    if (!dateString) return 'Güncellenmemiş';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffDays === 0) {
      if (diffMins < 1) return 'Şimdi';
      if (diffMins < 60) return `${diffMins} dk önce`;
      return `${diffHours} saat önce`;
    }
    if (diffDays === 1) return 'Dün';
    if (diffDays < 7) return `${diffDays} gün önce`;
    return date.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' });
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const connected = urlParams.get('connected');
    const error = urlParams.get('error');
    const platform = urlParams.get('platform');
    const cancelled = urlParams.get('cancelled');

    if (platform === 'tiktok') {
      if (connected === 'true') {
        showMessage('✅ TikTok hesabı başarıyla bağlandı!', 'success');
        window.history.replaceState({}, '', window.location.pathname);
        fetchTiktokAccounts();
      } else if (cancelled === 'true') {
        showMessage('Bağlantı iptal edildi', 'info');
        window.history.replaceState({}, '', window.location.pathname);
      } else if (error) {
        const errorMessage = decodeURIComponent(error);
        showMessage(errorMessage.includes('profil limiti') ? '❌ Profil limiti aşıldı.' : errorMessage, 'error');
        window.history.replaceState({}, '', window.location.pathname);
      }
    }
  }, []);

  useEffect(() => {
    fetchTiktokAccounts();
    fetchAutomationRules();
    fetchTags();
  }, []);

  if (loading && tiktokAccounts.length === 0) {
    return (
      <div className="tk-loading-state">
        <div className="tk-loading-spinner"></div>
        <p>Hesaplar yükleniyor...</p>
      </div>
    );
  }

  return (
    <div className="tk-tiktok-channels-container">
      {tiktokAccounts.length === 0 ? (
        <div className="tk-empty-state">
          <div className="tk-empty-icon">
            <svg viewBox="0 0 24 24" width="64" height="64" fill="currentColor">
              <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
            </svg>
          </div>
          <h3>Henüz TikTok hesabı bağlanmadı</h3>
          <p>İçeriklerinizi TikTok'ta paylaşmak için hesabınızı bağlayın</p>
          <button
            className="tk-empty-add-btn"
            onClick={handleConnectTikTok}
            disabled={connecting}
          >
            <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
              <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
            </svg>
            {connecting ? 'Bağlanıyor...' : 'TikTok Hesabı Bağla'}
          </button>
        </div>
      ) : (
        <div className="tk-channels-grid">
          {tiktokAccounts.map(account => {
            const automationData = getAccountAutomationSchedule(account._id);
            const activeTags = getAccountActiveAutomationTags(account._id);

            return (
              <div key={account._id} className="tk-channel-card">
                {/* HEADER */}
                <div className="tk-card-header">
                  <div className="tk-channel-avatar">
                    {account.profilePicture ? (
                      <img src={account.profilePicture} alt={account.username} />
                    ) : (
                      <span>🎵</span>
                    )}
                  </div>
                  <div className="tk-channel-info">
                    <div className="tk-channel-name-row">
                      <span className="tk-channel-name" title={account.displayName || account.username}>
                        {account.displayName || account.username}
                      </span>
                      <a
                        href={`https://tiktok.com/@${account.username}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="tk-external-link"
                        onClick={(e) => e.stopPropagation()}
                        title="TikTok'ta aç"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3" />
                        </svg>
                      </a>
                      <span className={`tk-status-dot ${account.isActive ? 'tk-active' : 'tk-inactive'}`}
                        title={account.isActive ? 'Aktif' : 'Pasif'} />
                    </div>
                    <a
                      href={`https://tiktok.com/@${account.username}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="tk-channel-handle"
                    >
                      @{account.username}
                    </a>
                  </div>
                </div>

                {/* STATS */}
                <div className="tk-stats-row">
                  <div className="tk-stat-box">
                    <span className="tk-stat-icon">👥</span>
                    <div className="tk-stat-content">
                      <span className="tk-stat-value">{formatCount(account.followerCount)}</span>
                      <span className="tk-stat-label">Takipçi</span>
                    </div>
                  </div>
                </div>

                {/* OTOMASYON */}
                {automationData && automationData.allTimes.length > 0 ? (
                  <div
                    className="tk-automation-section"
                    onClick={(e) => handleAutomationClick(account._id, e)}
                    title="Otomasyonu düzenlemek için tıklayın"
                  >
                    <div className="tk-automation-header-row">
                      <span className="tk-automation-icon">🤖</span>
                      <span className="tk-automation-label">
                        Her gün {automationData.allTimes.length} paylaşım
                        {automationData.rulesCount > 1 && ` · ${automationData.rulesCount} kural`}
                      </span>
                    </div>
                    <div className="tk-automation-times">
                      {automationData.allTimes.map((time, index) => (
                        <span
                          key={`${time.hour}-${time.minute}`}
                          className={`tk-time-chip ${index === automationData.nextTimeIndex ? 'tk-time-next' : ''}`}
                        >
                          {String(time.hour).padStart(2, '0')}:{String(time.minute).padStart(2, '0')}
                        </span>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="tk-automation-section tk-no-automation">
                    <span className="tk-automation-icon">🔕</span>
                    <span className="tk-no-automation-text">Otomasyon ayarlanmamış</span>
                  </div>
                )}

                {/* ETİKETLER */}
                {activeTags.length > 0 && (
                  <div className="tk-tags-section">
                    {activeTags.map(tag => (
                      <span
                        key={tag.id}
                        className="tk-tag-chip"
                        onClick={(e) => handleTagClick(tag.id, e)}
                        style={{ '--tag-color': tag.color }}
                        title={`${tag.name} etiketli içerikleri görüntüle`}
                      >
                        🏷️ {tag.name}
                      </span>
                    ))}
                  </div>
                )}

                {/* FOOTER */}
                <div className="tk-card-footer">
                  <div className="tk-footer-info">
                    <span className="tk-footer-profile">{account.profileName || 'GetLate'}</span>
                    <span className="tk-footer-date">{formatLastUpdate(account.createdAt)}</span>
                  </div>
                  <div className="tk-footer-actions">
                    <button
                      onClick={(e) => { e.stopPropagation(); disconnectAccount(account._id, account.username); }}
                      className="tk-action-btn tk-delete-btn"
                      title="Bağlantıyı Kes"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M18 6L6 18M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
});

TiktokChannels.displayName = 'TiktokChannels';
export default TiktokChannels;